
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type Language = 'pt' | 'en' | 'es' | 'fr';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, namespace?: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Get initial language from URL, localStorage, or auto-detect
const getInitialLanguage = (pathname: string): Language => {
  // First check URL path
  if (pathname.startsWith('/pt')) {
    return 'pt';
  } else if (pathname.startsWith('/es')) {
    return 'es';
  } else if (pathname.startsWith('/fr')) {
    return 'fr';
  }
  
  // Then check localStorage for manually set preference
  const savedLanguage = localStorage.getItem('language');
  if (savedLanguage === 'en' || savedLanguage === 'pt' || savedLanguage === 'es' || savedLanguage === 'fr') {
    // If there's a manually saved preference, return it
    return savedLanguage as Language;
  }
  
  // Default to English
  return 'en';
};

// For backwards compatibility
interface LegacyTranslations {
  [key: string]: any;
}

export const LanguageProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [language, setLanguage] = useState<Language>(() => 
    getInitialLanguage(location.pathname)
  );
  
  const [legacyTranslations, setLegacyTranslations] = useState<Record<Language, LegacyTranslations>>({} as Record<Language, LegacyTranslations>);

  // Update language when URL changes
  useEffect(() => {
    if (location.pathname.startsWith('/pt')) {
      setLanguage('pt');
    } else if (location.pathname.startsWith('/es')) {
      setLanguage('es');
    } else if (location.pathname.startsWith('/fr')) {
      setLanguage('fr');
    } else if (language !== 'en') {
      setLanguage('en');
    }
  }, [location.pathname]);
  
  // Navigate to the correct language path when language changes
  const changeLanguage = (newLanguage: Language) => {
    // Only process if language actually changed
    if (newLanguage === language) return;
    
    setLanguage(newLanguage);
    
    // Store in localStorage to persist the manually selected preference
    localStorage.setItem('language', newLanguage);
    
    // Get current path to preserve it when changing language
    let currentPath = location.pathname;
    const search = location.search || '';
    
    // Navigate to the appropriate URL
    if (newLanguage === 'pt') {
      // If changing to Portuguese
      if (!currentPath.startsWith('/pt')) {
        // Remove language prefix if any
        currentPath = currentPath.replace(/^\/(?:es|fr)/, '');
        if (currentPath === '') {
          navigate('/pt');
        } else {
          navigate(`/pt${currentPath}${search}`);
        }
      }
    } else if (newLanguage === 'es') {
      // If changing to Spanish
      if (!currentPath.startsWith('/es')) {
        // Remove language prefix if any
        currentPath = currentPath.replace(/^\/(?:pt|fr)/, '');
        if (currentPath === '') {
          navigate('/es');
        } else {
          navigate(`/es${currentPath}${search}`);
        }
      }
    } else if (newLanguage === 'fr') {
      // If changing to French
      if (!currentPath.startsWith('/fr')) {
        // Remove language prefix if any
        currentPath = currentPath.replace(/^\/(?:pt|es)/, '');
        if (currentPath === '') {
          navigate('/fr');
        } else {
          navigate(`/fr${currentPath}${search}`);
        }
      }
    } else {
      // If changing to English
      if (currentPath.startsWith('/pt') || currentPath.startsWith('/es') || currentPath.startsWith('/fr')) {
        // Remove language prefix
        currentPath = currentPath.replace(/^\/(?:pt|es|fr)/, '') || '/';
        navigate(`${currentPath}${search}`);
      }
    }
  };

  // Load legacy translations for backwards compatibility
  useEffect(() => {
    const loadLegacyTranslations = async () => {
      try {
        // Legacy translations import
        const ptModule = await import('../translations/pt.json');
        const enModule = await import('../translations/en.json');
        const esModule = await import('../translations/es.json');
        const frModule = await import('../translations/fr.json');
        
        setLegacyTranslations({
          pt: ptModule.default,
          en: enModule.default,
          es: esModule.default,
          fr: frModule.default
        });
      } catch (error) {
        console.error('Error loading legacy translations:', error);
      }
    };
    
    loadLegacyTranslations();
  }, []);

  // Function to translate text that works with both legacy and new system
  const t = (key: string, namespace?: string): string => {
    if (!key) return '';
    
    // If using legacy method (no namespace)
    if (!namespace && legacyTranslations[language]) {
      const keys = key.split('.');
      let value: any = legacyTranslations[language];
      
      for (const k of keys) {
        if (value[k] === undefined) return key;
        value = value[k];
      }
      
      return typeof value === 'string' ? value : key;
    }
    
    // If using new namespaced method but we don't have the translations yet,
    // return the key as fallback - component will handle it when loaded
    return key;
  };

  const value = {
    language,
    setLanguage: changeLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
